import { z } from 'zod';
import type { HolidayScheme, WorkloadScheme } from '@/components/common/settings/capacity/types';
import type { EmploymentType, LocationSummary, Resource, ResourceType } from './capacity/resources';
import type { Skill } from './skills';

type TeamSourceType = 'JIRA_TEAM' | 'TEMPO_TEAM';

export interface CreateTeamRequest {
  name: string;
  loopId?: number;
}

export interface TeamPatchRequest extends Omit<Partial<CreateTeamRequest>, 'loopId'> {
  loopId?: number | null;
}

export interface BulkCreateTeamItem {
  name: string;
  memberNames?: string[];
}

export interface BulkCreateTeamsRequest {
  teams: BulkCreateTeamItem[];
  loopId?: number;
}

export interface BulkCreateTeamsResponse {
  teams: Team[];
  skippedCount: number;
  createdCount: number;
}

export interface Team extends CreateTeamRequest {
  id: number;
  icon?: string;
  color: string;
  membershipCount?: number;
  totalCapacitySeconds: number | null;
  createdAt: string;
  updatedAt: string;
  description: string | null;
  sourceType?: TeamSourceType;
  instanceId?: string;
  lastSyncedAt?: string;
  memberships?: TeamMembershipWithResource[];
}

export type TeamMembership = {
  membershipId: number;
  startDate?: string;
  endDate?: string;
  commitment?: number;
  roleId?: number;
  roleName?: string;
};

export type ResourceTeamMembership = {
  membershipId: number;
  resourceId: number;
  teamId: number;
  teamName: string;
  startDate?: string;
  endDate?: string;
  commitment?: number;
  sourceCommitment?: number;
  commitmentOverridden?: boolean;
  roleId?: number;
  roleName?: string;
  membershipSourceId?: string;
};

export type PendingMembershipChanges = {
  [membershipId: number]: Partial<MembershipUpdateRequest>;
};

export type MembershipUpdateRequest = {
  startDate?: string | null;
  endDate?: string | null;
  commitment?: number;
  roleId?: number | null;
};

export type MembershipBulkUpdateItem = {
  membershipId: number;
  update: MembershipUpdateRequest;
};

export type MembershipBulkUpdateRequest = {
  updates: MembershipBulkUpdateItem[];
};

export type TeamMembershipRequest = MembershipUpdateRequest & {
  teamId: number;
};

export type MembershipBulkAddRequest = {
  memberships: TeamMembershipRequest[];
};

export type MembershipBulkDeleteRequest = {
  membershipIds: number[];
};

export type MembershipBulkOperation = {
  resourceIds: number[];
  teamIds: number[];
};

export type BulkPatchTeamItem = {
  id: number;
  patch: TeamPatchRequest;
};

export type BulkPatchTeamsRequest = {
  teams: BulkPatchTeamItem[];
};

export type PortfolioChangeBlockReason = 'TEAM_HAS_ASSIGNED_ITEMS';

export interface PortfolioChangeEligibility {
  eligible: boolean;
  reason: PortfolioChangeBlockReason | null;
  assignedItemCount: number;
}

export interface BlockedTeamPatch {
  teamId: number;
  teamName: string;
  reason: PortfolioChangeBlockReason;
  assignedItemCount: number;
  currentLoopId: number;
}

export interface BulkPatchTeamsResponse {
  updated: Team[];
  blocked: BlockedTeamPatch[];
}

export type BulkTeamActionRequest = {
  membershipsToAdd?: MembershipBulkOperation;
  membershipsToRemove?: MembershipBulkOperation;
};

export type MembershipResponse = {
  id: number;
  resourceId: number;
  teamId: number;
  startDate?: string;
  endDate?: string;
  commitment?: number;
};

export type CreateOpenPositionMembershipRequest = {
  openPositionId: number;
  roleId: number;
  startDate?: string | null;
  endDate?: string | null;
  commitment?: number;
};

export type OpenPositionMembershipResponse = {
  id: number;
  openPositionId: number;
  teamId: number;
  startDate?: string;
  endDate?: string;
  commitment?: number;
};

export type BulkTeamActionResponse = {
  membershipsCreated: MembershipResponse[];
  membershipsUpdated: MembershipResponse[];
  membershipsDeleted: number[];
};

export type TeamMemberWithCapacity = {
  resourceId: number;
  resourceType: ResourceType;
  resourceName: string;
  resourceStatus: ResourceStatus;
  employmentType: EmploymentType;
  resourceSourceId?: string;
  capacitySeconds?: number;
  memberships: TeamMembership[];
  skills?: Skill[];
};

export interface OpenPositionSkillSummary {
  id: number;
  name: string;
  isCritical: boolean;
  assignmentId?: number;
}

export interface OpenPositionSummary {
  openPositionId: number;
  openPositionName: string;
  role?: TeamRoleSummary | null;
  skills: OpenPositionSkillSummary[];
}

export interface TeamLookupResponse {
  id: number;
  name: string;
  description: string | null;
  totalCapacitySeconds: number | null;
  members?: TeamMemberWithCapacity[];
  openPositions?: OpenPositionSummary[];
}

export type TeamsLookupResponse = {
  data: TeamLookupResponse[];
};

export type TeamsResponse = {
  data: Team[];
};

export type WorkspaceUserGroup = {
  id: number;
  group: {
    id: number;
  };
};

export type WorkspaceUserGroupsById = Record<string, WorkspaceUserGroup>;

export interface TeamsWithIncludedResponse {
  data: Team[];
  included?: {
    workspaceUserGroups: WorkspaceUserGroupsById;
  };
}
export type StatusAction = 'DEACTIVATE' | 'REACTIVATE';
export const STATUS_ACTION = {
  DEACTIVATE: 'DEACTIVATE' as StatusAction,
  REACTIVATE: 'REACTIVATE' as StatusAction,
};

export type ResourceStatus = 'ACTIVE' | 'DEACTIVATED';
export const RESOURCE_STATUS = {
  ACTIVE: 'ACTIVE' as ResourceStatus,
  DEACTIVATED: 'DEACTIVATED' as ResourceStatus,
};
export type ResourceFilterStatus = ResourceStatus | 'ALL';

export type SearchMembershipsSearchParams = {
  query?: string;
  resourceStatus?: ResourceFilterStatus;
  roleId?: number[];
  skillId?: number[];
  locationId?: number[];
  showPastMemberships?: boolean;
  showFutureMemberships?: boolean;
};

export type TeamRoleSummary = {
  id: number;
  name: string;
};

export type TeamMembershipWithResource = {
  membershipId: number;
  resourceId: number;
  resourceType: ResourceType;
  resourceName: string;
  resourceStatus: ResourceStatus;
  resourceIsAnonymized?: boolean;
  resourceSourceId?: string;
  membershipSourceId?: string;
  otherTeams?: Team[];
  workloadSchemes?: WorkloadScheme[];
  holidaySchemes?: HolidayScheme[];
  startDate?: string;
  endDate?: string;
  commitment?: number;
  sourceCommitment?: number;
  commitmentOverridden?: boolean;
  location?: LocationSummary | null;
  role?: TeamRoleSummary;
  skills?: Skill[];
} & (
  | { openPositionId?: null; openPositionName?: null }
  | { openPositionId: number; openPositionName: string }
);

export interface TeamWithIncludedResponse {
  team: Team;
  memberships?: TeamMembershipWithResource[];
}

export type GetTeamsParams = { includeMembers?: boolean };

export const CREATE_MODE = 'CREATE';
export const EDIT_MODE = 'EDIT';
export type TeamFormMode = typeof CREATE_MODE | typeof EDIT_MODE;

export interface TeamRequestWithMembers extends Omit<CreateTeamRequest, 'loopId'> {
  loopId?: number | null;
  members?: Resource[];
}

export interface ImportTeamsSearch {
  sourceId: string;
  name: string;
  imported: boolean;
}

export interface ImportTeamsSearchResponse {
  data: ImportTeamsSearch[];
}

export interface ImportTeamsSearchParameters {
  sourceType: TeamSourceType;
  instanceId: string;
}

export interface ImportTeamsRequest {
  sourceType: TeamSourceType;
  instanceId: string;
  teams: ImportTeamsSearch[];
}

export interface DateRangeInput {
  from: string;
  to: string;
}

export type TeamLookupExpand = 'MEMBERSHIPS' | 'CAPACITY' | 'SKILLS' | 'OPEN_POSITIONS';

export interface TeamsLookupRequest {
  ids?: number[];
  dateRange: DateRangeInput;
  teamRoleIds?: number[];
  skillIds?: number[];
  loopId?: string;
  expand?: TeamLookupExpand[];
}

export interface SyncTeamsRequest {
  teamType: TeamSourceType;
  instanceId: string;
}

type SyncStatusType = 'JIRA_TEAM' | 'TEMPO_TEAM' | 'RESOURCE' | 'AVAILABILITY_SCHEME';

type SyncStatusStatus = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'FAILED';

export interface SyncStatusResponse {
  data: SyncStatus[];
}

export interface SyncStatus {
  instanceId: string;
  type: SyncStatusType;
  status: SyncStatusStatus;
}

export const MEMBERS = 'MEMBERS' as const;
export const TEAM = 'TEAM' as const;
export const SKELETON = 'SKELETON' as const;
export type TeamSkeletonRow = { type: typeof SKELETON; id: string };
export type TeamRow =
  | { type: typeof TEAM; id: string; team: Team; isSyncing: boolean }
  | TeamSkeletonRow;
export type TeamsPageTabType = typeof MEMBERS | typeof TEAM;

export type ResourceFormData = {
  name: string;
  email: string;
  employmentType: EmploymentType;
};

export interface PendingNewMembership {
  teamId: number;
  teamName: string;
  tempId: string;
  commitment?: number;
  startDate?: string | null;
  endDate?: string | null;
  roleId?: number | null;
}

export const TEAM_SORT_FIELD = {
  ID: 'ID',
  NAME: 'NAME',
  DESCRIPTION: 'DESCRIPTION',
  CREATED_AT: 'CREATED_AT',
  UPDATED_AT: 'UPDATED_AT',
  MEMBERSHIP_COUNT: 'MEMBERSHIP_COUNT',
} as const;

export type TeamSortField = (typeof TEAM_SORT_FIELD)[keyof typeof TEAM_SORT_FIELD];

export const SORT_DIRECTION = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const;

export type SortDirection = (typeof SORT_DIRECTION)[keyof typeof SORT_DIRECTION];

export type TeamSearchParams = {
  query?: string;
  sortBy?: TeamSortField;
  sortDirection?: SortDirection;
  limit?: number;
  cursor?: string;
  roleIds?: number[];
  loopIds?: string[];
};

export const membershipOverlapConflictSchema = z.object({
  resourceId: z.number(),
  existingStartDate: z.string().nullable(),
  existingEndDate: z.string().nullable(),
});

export type MembershipOverlapConflict = z.infer<typeof membershipOverlapConflictSchema>;

export const allocationOutcomeSchema = z.enum([
  'HEADROOM_USED',
  'HEADROOM_CLAMPED',
  'AUTO_SPLIT',
  'NO_HEADROOM',
]);

export type AllocationOutcome = z.infer<typeof allocationOutcomeSchema>;

export const affectedSiblingSchema = z.object({
  membershipId: z.number(),
  teamId: z.number(),
  teamName: z.string().nullable(),
  currentCommitment: z.number(),
  newCommitment: z.number(),
});

export type AffectedSibling = z.infer<typeof affectedSiblingSchema>;

export const membershipPreviewResponseSchema = z.object({
  proposedCommitment: z.number(),
  outcome: allocationOutcomeSchema,
  suggested: z.number().nullable(),
  affectedSiblings: z.array(affectedSiblingSchema),
  currentOtherTotal: z.number(),
});

export type MembershipPreviewResponse = z.infer<typeof membershipPreviewResponseSchema>;

export const allocationErrorSchema = z.object({
  code: z.literal('ALLOCATION_EXCEEDED'),
  message: z.string(),
  memberId: z.number(),
  currentTotal: z.number(),
  attemptedTotal: z.number(),
  oldTotal: z.number(),
});

export type AllocationError = z.infer<typeof allocationErrorSchema>;

export type TeamsPageResponse = {
  data: Team[];
  recordLimit: number | null;
  recordCount: number;
  cursor: string | null;
  hasNextPage: boolean;
};
